import { useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  emptyLine,
  linesTotalCents,
  type LineDraft,
  type LineItemKind,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { money } from '../money';
import { IconPlus, IconTrash } from '../icons';

/**
 * The ONE line editor. Quotes, invoices and request triage all price work the same way,
 * so they share this rather than growing three lookalike forms that drift apart.
 *
 * The parsing rules live in @servwell/shared (line-draft.ts) because they are money
 * rules, not layout — this file only renders them.
 */
export { emptyLine, parseLines, linesTotalCents } from '@servwell/shared';
export type { LineDraft, ParsedLine } from '@servwell/shared';

const field = 'h-10 rounded-[var(--radius)] border border-border bg-surface px-2 text-sm';

/**
 * What typing `name` into a row changes: the name, and when it is a saved item's name, that
 * item's price, kind and id. A name that matches nothing clears the id, so a row edited away
 * from its item stops claiming to be it.
 */
export function typedName(
  name: string,
  priceBook: { id: string; name: string; unitPriceCents: number; kind: string }[],
): Partial<LineDraft> {
  const hit = priceBook.find((p) => p.name.toLowerCase() === name.trim().toLowerCase());
  return {
    name,
    priceBookItemId: hit?.id,
    ...(hit
      ? { price: (hit.unitPriceCents / 100).toFixed(2), kind: hit.kind as LineItemKind }
      : {}),
  };
}

export function LineItemBuilder({
  lines,
  onChange,
  label = 'What are you charging for?',
}: {
  lines: LineDraft[];
  onChange: (next: LineDraft[]) => void;
  label?: string;
}) {
  const trpc = useTRPC();
  const priceBook = useQuery(trpc.priceBook.list.queryOptions());
  const listId = useId();

  const patch = (i: number, change: Partial<LineDraft>) =>
    onChange(lines.map((l, j) => (j === i ? { ...l, ...change } : l)));

  /**
   * Typing a saved item's name fills in its price and kind, and remembers WHICH item it is,
   * so a part used on the job can be matched to the line (Inventory v2 Phase 1). A datalist
   * keeps this a plain text box — you can always type something that isn't in the price
   * book, which is the common case on a service call; such a row carries no item.
   */
  const applyPriceBook = (i: number, name: string) =>
    patch(i, typedName(name, priceBook.data ?? []));

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>

      <datalist id={listId}>
        {(priceBook.data ?? []).map((p) => (
          <option key={p.id} value={p.name}>
            {money(p.unitPriceCents)}
          </option>
        ))}
      </datalist>

      {lines.map((l, i) => (
        <div key={i} className="flex gap-2">
          <input
            className={`${field} min-w-0 flex-1`}
            placeholder="Description"
            list={listId}
            value={l.name}
            aria-label={`Line ${i + 1} description`}
            onChange={(e) => applyPriceBook(i, e.target.value)}
          />
          <input
            className={`${field} w-14`}
            placeholder="Qty"
            inputMode="decimal"
            value={l.qty}
            aria-label={`Line ${i + 1} quantity`}
            onChange={(e) => patch(i, { qty: e.target.value })}
          />
          <input
            className={`${field} w-24`}
            placeholder="$ each"
            inputMode="decimal"
            value={l.price}
            aria-label={`Line ${i + 1} price`}
            onChange={(e) => patch(i, { price: e.target.value })}
          />
          {lines.length > 1 ? (
            <button
              type="button"
              aria-label={`Remove line ${i + 1}`}
              onClick={() => onChange(lines.filter((_, j) => j !== i))}
              className="rounded-[var(--radius-sm)] px-1 text-muted-foreground hover:bg-surface-2 hover:text-[var(--sw-danger-text)]"
            >
              <IconTrash size={15} />
            </button>
          ) : null}
        </div>
      ))}

      <div className="flex items-center justify-between">
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--sw-brand-text)] hover:underline"
          onClick={() => onChange([...lines, emptyLine()])}
        >
          <IconPlus size={13} />
          Add another line
        </button>
        <p className="font-medium tabular-nums">Total {money(linesTotalCents(lines))}</p>
      </div>
    </div>
  );
}
